if (!customElements.get('product-card-carousel')) {
  const visibleImageQueue = [];
  const hiddenImageQueue = [];
  const queuedDeferredImages = new WeakSet();
  const imagePreloadRequests = new WeakMap();
  const maxConcurrentVisibleImages = 3;
  const maxConcurrentHiddenImages = 2;
  let activeVisibleImages = 0;
  let activeHiddenImages = 0;
  let backgroundPreloadScheduled = false;
  let backgroundPreloadStarted = false;

  const waitForImage = (image) => {
    if (image.complete && image.naturalWidth) return Promise.resolve();
    if (typeof image.decode === 'function') return image.decode().catch(() => {});

    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  };

  const preloadImageSource = (image) => {
    if (image.complete && image.naturalWidth) return Promise.resolve();

    const existingRequest = imagePreloadRequests.get(image);
    if (existingRequest) return existingRequest;

    const source = image.currentSrc || image.getAttribute('src');
    const sourceSet = image.getAttribute('srcset');
    if (!source && !sourceSet) return Promise.resolve();

    const preload = new Image();
    const sizes = image.getAttribute('sizes');
    if (sizes) preload.sizes = sizes;
    preload.decoding = 'async';
    preload.fetchPriority = image.fetchPriority || 'auto';
    preload.setAttribute('fetchpriority', image.getAttribute('fetchpriority') || 'auto');
    if (sourceSet) preload.srcset = sourceSet;
    if (source) preload.src = source;

    const request = waitForImage(preload).finally(() => {
      imagePreloadRequests.delete(image);
    });
    imagePreloadRequests.set(image, request);
    return request;
  };

  const scheduleBackgroundPreload = () => {
    const hasVisibleWork = visibleImageQueue.length > 0 || activeVisibleImages > 0;
    const activeImages = hasVisibleWork ? activeVisibleImages : activeHiddenImages;
    const maxConcurrentImages = hasVisibleWork ? maxConcurrentVisibleImages : maxConcurrentHiddenImages;
    const imageQueue = hasVisibleWork ? visibleImageQueue : hiddenImageQueue;

    if (backgroundPreloadScheduled || activeImages >= maxConcurrentImages || !imageQueue.length) {
      return;
    }

    backgroundPreloadScheduled = true;
    const runWhenIdle = window.requestIdleCallback
      ? (callback) => window.requestIdleCallback(callback, { timeout: 1200 })
      : (callback) => window.setTimeout(() => callback({ didTimeout: true, timeRemaining: () => 0 }), 160);

    runWhenIdle((deadline) => {
      backgroundPreloadScheduled = false;

      const shouldLoadVisibleImages = visibleImageQueue.length > 0 || activeVisibleImages > 0;
      const activeCount = shouldLoadVisibleImages ? activeVisibleImages : activeHiddenImages;
      const concurrentLimit = shouldLoadVisibleImages ? maxConcurrentVisibleImages : maxConcurrentHiddenImages;
      const queue = shouldLoadVisibleImages ? visibleImageQueue : hiddenImageQueue;
      let availableSlots = concurrentLimit - activeCount;

      while (
        availableSlots > 0 &&
        queue.length &&
        (deadline.didTimeout || deadline.timeRemaining() > 4)
      ) {
        const image = queue.shift();
        availableSlots -= 1;

        if (shouldLoadVisibleImages) {
          activeVisibleImages += 1;
          image.fetchPriority = 'auto';
          image.setAttribute('fetchpriority', 'auto');
        } else {
          activeHiddenImages += 1;
          image.fetchPriority = 'low';
          image.setAttribute('fetchpriority', 'low');
        }
        image.loading = 'eager';

        Promise.all([waitForImage(image), preloadImageSource(image)]).finally(() => {
          if (shouldLoadVisibleImages) {
            activeVisibleImages -= 1;
          } else {
            activeHiddenImages -= 1;
          }
          scheduleBackgroundPreload();
        });
      }

      scheduleBackgroundPreload();
    });
  };

  const queueCardCarouselImages = (root) => {
    const selector = '.protemoa-product-block product-card-carousel';
    const carousels = root.matches?.(selector)
      ? [root]
      : Array.from(root.querySelectorAll?.(selector) || []);

    carousels.forEach((carousel) => {
      carousel.querySelectorAll('[data-carousel-slide] img').forEach((image, index) => {
        if (queuedDeferredImages.has(image)) return;
        queuedDeferredImages.add(image);

        if (index === 0) {
          visibleImageQueue.push(image);
        } else {
          hiddenImageQueue.push(image);
        }
      });
    });

    scheduleBackgroundPreload();
  };

  const startBackgroundPreload = () => {
    if (window.__protemoaImageLoadingPriority) return;
    if (backgroundPreloadStarted) return;
    backgroundPreloadStarted = true;
    queueCardCarouselImages(document);
  };

  if (document.readyState === 'complete') {
    window.setTimeout(startBackgroundPreload, 0);
  } else {
    window.addEventListener('load', startBackgroundPreload, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (backgroundPreloadStarted) queueCardCarouselImages(event.target);
  });

  class ProductCardCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.indicators = Array.from(this.querySelectorAll('[data-carousel-indicator]'));
      this.progress = this.querySelector('.protemoa-product-card__progress');
      this.progressThumb = this.querySelector('[data-carousel-progress-thumb]');

      if (backgroundPreloadStarted) queueCardCarouselImages(this);

      if (!this.viewport || this.slides.length < 2) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleMouseSnapEnd = this.handleMouseSnapEnd.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handlePageScroll = this.handlePageScroll.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.handleKeydown);
      this.viewport.addEventListener('mousedown', this.handleMouseDown);
      this.viewport.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.viewport.addEventListener('pointerleave', this.handlePointerLeave);
      this.viewport.addEventListener('click', this.handleClick, true);
      this.viewport.addEventListener('dragstart', this.handleDragStart);
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('scroll', this.handlePageScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
        if (this.progress) this.resizeObserver.observe(this.progress);
      }
      this.refreshProgressMetrics();
      this.updateActiveSlide();
    }

    disconnectedCallback() {
      if (!this.viewport) return;
      this.viewport.removeEventListener('scroll', this.handleScroll);
      this.viewport.removeEventListener('keydown', this.handleKeydown);
      this.viewport.removeEventListener('mousedown', this.handleMouseDown);
      this.viewport.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport.removeEventListener('pointerleave', this.handlePointerLeave);
      this.viewport.removeEventListener('click', this.handleClick, true);
      this.viewport.removeEventListener('dragstart', this.handleDragStart);
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handlePageScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.viewport.removeEventListener('scrollend', this.handleMouseSnapEnd);
      this.classList.remove('is-dragging');
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      if (this.clickResetTimer) clearTimeout(this.clickResetTimer);
      if (this.mouseSnapTimer) clearTimeout(this.mouseSnapTimer);
      if (this.hoverSlideTimer) clearTimeout(this.hoverSlideTimer);
    }

    handleScroll() {
      if (this.animationFrame) return;
      this.animationFrame = requestAnimationFrame(() => {
        this.animationFrame = null;
        this.updateActiveSlide();
      });
    }

    handleResize() {
      this.refreshProgressMetrics();
      this.updateActiveSlide();
    }

    handleKeydown(event) {
      if (this.isMobileStaticMode()) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const currentIndex = this.getActiveIndex();
      const nextIndex = Math.max(0, Math.min(this.slides.length - 1, currentIndex + direction));
      this.viewport.scrollTo({ left: nextIndex * this.getSlideWidth(), behavior: 'smooth' });
    }

    isMobileSwipeMode() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    isMobileStaticMode() {
      return this.hasAttribute('data-mobile-static') && window.matchMedia('(max-width: 749px)').matches;
    }

    handleMouseDown(event) {
      if (this.isMobileStaticMode() || !this.isMobileSwipeMode() || event.button !== 0 || this.isMouseSwiping) return;

      this.clearMouseSnapState();
      this.isMouseSwiping = true;
      this.mouseSwipeMoved = false;
      this.mouseSwipeStartX = event.clientX;
      this.mouseSwipeStartScrollLeft = this.viewport.scrollLeft;
      this.mouseSwipeStartIndex = this.getActiveIndex();
      this.mouseSwipeStartTime = performance.now();
      this.classList.add('is-dragging');
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
      window.addEventListener('blur', this.handleMouseUp);
    }

    handleMouseMove(event) {
      if (!this.isMouseSwiping) return;

      const pointerDelta = event.clientX - this.mouseSwipeStartX;
      if (Math.abs(pointerDelta) < 4 && !this.mouseSwipeMoved) return;

      event.preventDefault();
      this.mouseSwipeMoved = true;
      this.viewport.scrollLeft = this.mouseSwipeStartScrollLeft - pointerDelta;
      this.updateActiveSlide();
    }

    handleMouseUp() {
      if (!this.isMouseSwiping) return;
      this.finishMouseSwipe();
    }

    handlePointerMove(event) {
      if (!window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) return;
      if (performance.now() < (this.hoverSuppressedUntil || 0)) return;

      const bounds = this.viewport.getBoundingClientRect();
      if (!bounds.width) return;

      const pointerPosition = Math.max(0, Math.min(bounds.width - 1, event.clientX - bounds.left));
      const nextIndex = Math.floor((pointerPosition / bounds.width) * this.slides.length);
      if (nextIndex === this.getActiveIndex()) {
        this.clearHoverSlideCandidate();
        return;
      }

      if (nextIndex === this.hoverSlideCandidate) return;

      this.clearHoverSlideCandidate();
      this.hoverSlideCandidate = nextIndex;
      this.hoverSlideTimer = setTimeout(() => {
        this.hoverSlideTimer = null;
        const candidateIndex = this.hoverSlideCandidate;
        this.hoverSlideCandidate = null;
        if (candidateIndex === null || candidateIndex === this.getActiveIndex()) return;

        this.viewport.scrollLeft = candidateIndex * this.getSlideWidth();
        this.updateActiveSlide();
      }, 90);
    }

    finishMouseSwipe() {
      const moved = this.mouseSwipeMoved;
      const viewportWidth = this.getSlideWidth();
      const scrollDistance = this.viewport.scrollLeft - this.mouseSwipeStartScrollLeft;
      const swipeDuration = Math.max(performance.now() - this.mouseSwipeStartTime, 1);
      this.isMouseSwiping = false;
      this.mouseSwipeMoved = false;
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);
      window.removeEventListener('blur', this.handleMouseUp);

      if (!moved) {
        this.clearMouseSnapState();
        return;
      }

      this.suppressNextClick = true;
      if (this.clickResetTimer) clearTimeout(this.clickResetTimer);
      this.clickResetTimer = setTimeout(() => {
        this.suppressNextClick = false;
      }, 250);

      const distanceThreshold = Math.min(36, Math.max(14, viewportWidth * 0.08));
      const swipeVelocity = Math.abs(scrollDistance) / swipeDuration;
      const isIntentionalSwipe =
        Math.abs(scrollDistance) >= distanceThreshold ||
        (Math.abs(scrollDistance) >= 8 && swipeVelocity >= 0.12);
      const direction = scrollDistance > 0 ? 1 : -1;
      const slideCount = Math.max(1, Math.round(Math.abs(scrollDistance) / viewportWidth));
      const nextIndex = isIntentionalSwipe
        ? Math.max(0, Math.min(this.slides.length - 1, this.mouseSwipeStartIndex + direction * slideCount))
        : this.mouseSwipeStartIndex;

      this.viewport.addEventListener('scrollend', this.handleMouseSnapEnd, { once: true });
      this.mouseSnapTimer = setTimeout(this.handleMouseSnapEnd, 600);
      this.viewport.scrollTo({ left: nextIndex * this.getSlideWidth(), behavior: 'smooth' });
    }

    handleMouseSnapEnd() {
      this.clearMouseSnapState();
      this.updateActiveSlide();
    }

    clearMouseSnapState() {
      if (this.mouseSnapTimer) {
        clearTimeout(this.mouseSnapTimer);
        this.mouseSnapTimer = null;
      }
      this.viewport.removeEventListener('scrollend', this.handleMouseSnapEnd);
      this.classList.remove('is-dragging');
    }

    handleClick(event) {
      if (!this.suppressNextClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressNextClick = false;
    }

    handleDragStart(event) {
      if (this.isMobileStaticMode()) return;
      if (!this.isMobileSwipeMode()) return;
      event.preventDefault();
    }

    handlePointerLeave() {
      if (!window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches) return;

      this.clearHoverSlideCandidate();
      this.viewport.scrollLeft = 0;
      this.updateActiveSlide();
    }

    handlePageScroll() {
      this.hoverSuppressedUntil = performance.now() + 140;
      this.clearHoverSlideCandidate();
    }

    clearHoverSlideCandidate() {
      if (this.hoverSlideTimer) {
        clearTimeout(this.hoverSlideTimer);
        this.hoverSlideTimer = null;
      }
      this.hoverSlideCandidate = null;
    }

    getActiveIndex() {
      const viewportWidth = this.getSlideWidth();
      if (!viewportWidth) return 0;
      return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.viewport.scrollLeft / viewportWidth)));
    }

    getSlideWidth() {
      return this.progressMetrics?.viewportWidth || this.viewport.getBoundingClientRect().width || this.viewport.clientWidth;
    }

    updateActiveSlide() {
      const activeIndex = this.getActiveIndex();
      if (activeIndex !== this.activeIndex) {
        this.activeIndex = activeIndex;
        this.dataset.activeSlide = String(activeIndex + 1);
        this.indicators.forEach((indicator, index) => {
          indicator.classList.toggle('is-active', index === activeIndex);
        });
      }
      this.updateProgressThumb();
    }

    updateProgressThumb() {
      if (!this.progress || !this.progressThumb) return;

      if (!this.progressMetrics) this.refreshProgressMetrics();
      const { maxScroll, maxThumbOffset } = this.progressMetrics;
      const scrollPosition = Math.max(0, Math.min(maxScroll, this.viewport.scrollLeft));
      const scrollProgress = maxScroll ? scrollPosition / maxScroll : 0;
      const thumbOffset = scrollProgress * maxThumbOffset;

      this.progressThumb.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
    }

    refreshProgressMetrics() {
      if (!this.progress || !this.progressThumb) return;

      const progressStyle = window.getComputedStyle(this.progress);
      const horizontalPadding =
        (Number.parseFloat(progressStyle.paddingLeft) || 0) +
        (Number.parseFloat(progressStyle.paddingRight) || 0);
      const trackWidth = Math.max(this.progress.clientWidth - horizontalPadding, 0);
      const thumbWidth = trackWidth / this.slides.length;
      const maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
      const viewportWidth = this.viewport.getBoundingClientRect().width || this.viewport.clientWidth;

      this.progressMetrics = {
        maxScroll,
        maxThumbOffset: Math.max(trackWidth - thumbWidth, 0),
        viewportWidth,
      };
      this.progressThumb.style.width = `${thumbWidth}px`;
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);
}
